import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { formatMoney, formatDateTime, formatDate } from "../../lib/money";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Upload, FileSpreadsheet, ArrowRight, Banknote, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../contexts/AuthContext";

const STATUS_STYLE = {
  ready: "bg-emerald-50 text-emerald-700",
  processing: "bg-blue-50 text-blue-700",
  failed: "bg-rose-50 text-rose-700",
};

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function UploadWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(1);
  const [cards, setCards] = useState([]);
  const [cardId, setCardId] = useState("");
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({ date: "", amount: "", description: "", external_ref: "" });
  const [amountSign, setAmountSign] = useState("debit_positive");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setStep(1); setCardId(""); setFile(null); setPreview(null);
    setMapping({ date: "", amount: "", description: "", external_ref: "" });
    setAmountSign("debit_positive"); setError(""); setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    api.get("/cards/available")
      .then((r) => setCards((r.data || []).filter((c) => c.active)))
      .catch((e) => setError(extractError(e)));
  }, [open, reset]);

  const goPreview = async () => {
    setError("");
    if (!file) { setError("Choose a CSV file to continue."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("File exceeds 10MB limit."); return; }
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/statements/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setMapping({
        date: r.data.detected_mapping?.date || "",
        amount: r.data.detected_mapping?.amount || "",
        description: r.data.detected_mapping?.description || "",
        external_ref: r.data.detected_mapping?.external_ref || "",
      });
      setStep(3);
    } catch (e) {
      setError(extractError(e));
    } finally { setPreviewing(false); }
  };

  const submitIngest = async () => {
    setError("");
    if (!mapping.date || !mapping.amount || !mapping.description) {
      setError("Date, amount and description columns are required.");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("card_id", cardId);
      form.append("column_mapping", JSON.stringify({
        date: mapping.date, amount: mapping.amount, description: mapping.description,
        external_ref: mapping.external_ref || null,
      }));
      form.append("amount_sign", amountSign);
      if (file?.name) form.append("original_filename", file.name);
      const r = await api.post("/statements/ingest", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Ingested ${r.data.total_lines} lines`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      setError(extractError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-wizard" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload statement</DialogTitle>
          <DialogDescription>Step {step} of 4 — wire a CSV from your bank or card provider.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Card</Label>
              <Select value={cardId} onValueChange={setCardId}>
                <SelectTrigger data-testid="wizard-card-select"><SelectValue placeholder="Choose a card" /></SelectTrigger>
                <SelectContent>
                  {cards.length === 0 ? (
                    <SelectItem value="__none__" disabled>No active cards</SelectItem>
                  ) : cards.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label} · {c.last4} ({c.type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button disabled={!cardId} onClick={() => setStep(2)} data-testid="wizard-step1-next"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">CSV file (max 10MB)</Label>
              <Input id="csv-file" type="file" accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="wizard-file-input" />
            </div>
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button disabled={!file || previewing} onClick={goPreview} data-testid="wizard-preview-btn"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {previewing ? "Previewing…" : "Preview"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && preview && (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    {preview.headers.map((h) => (<TableHead key={h} className="whitespace-nowrap text-xs">{h}</TableHead>))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.sample_rows.slice(0, 5).map((r, i) => {
                    // Derive a stable key from row content; preview rows have no id.
                    const rowKey = preview.headers.map((h) => r[h] ?? "").join("|") || `row-${i}`;
                    return (
                      <TableRow key={rowKey}>
                        {preview.headers.map((h) => (
                          <TableCell key={h} className="whitespace-nowrap text-xs">{r[h] ?? ""}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-slate-500">Detected {preview.total_rows} rows. Map the required columns:</p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { k: "date", label: "Date column *" },
                { k: "amount", label: "Amount column *" },
                { k: "description", label: "Description column *" },
                { k: "external_ref", label: "Reference (optional)" },
              ].map(({ k, label }) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Select value={mapping[k] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [k]: v === "__none__" ? "" : v }))}>
                    <SelectTrigger data-testid={`wizard-map-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {k === "external_ref" && <SelectItem value="__none__">— (none)</SelectItem>}
                      {preview.headers.map((h) => (<SelectItem key={h} value={h}>{h}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <Label className="text-xs">Amount sign convention</Label>
              <RadioGroup value={amountSign} onValueChange={setAmountSign} className="mt-2 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="debit_positive" id="r-pos" data-testid="wizard-sign-pos" />
                  <Label htmlFor="r-pos" className="text-sm font-normal">Debit / spend is positive (most credit-card CSVs)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="debit_negative" id="r-neg" data-testid="wizard-sign-neg" />
                  <Label htmlFor="r-neg" className="text-sm font-normal">Debit / spend is negative (most bank-account CSVs)</Label>
                </div>
              </RadioGroup>
            </div>

            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={submitIngest} disabled={submitting} data-testid="wizard-ingest-btn"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {submitting ? "Ingesting…" : "Ingest statement"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function StatementsList() {
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const initialStatus = searchParams.get("match_status") || "";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, original_filename, ...}
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/statements", { params: { page_size: 50 } });
      setBatches(r.data?.items || []);
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/statements/${deleteTarget.id}`);
      toast.success(
        `Deleted batch · ${r.data.lines_deleted} lines, ${r.data.expenses_reverted} expenses reverted, ${r.data.chases_cancelled} chase(s) cancelled`
      );
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  };

  const triageBanner = useMemo(() => {
    if (!initialQ && !initialStatus) return null;
    return (
      <Alert className="mb-4 border-amber-200 bg-amber-50" data-testid="triage-banner">
        <AlertDescription className="text-amber-900">
          Triage filter active: <span className="font-mono text-xs">{initialQ || initialStatus}</span>. Open any batch to view filtered lines.
        </AlertDescription>
      </Alert>
    );
  }, [initialQ, initialStatus]);

  return (
    <div data-testid="statements-list">
      <PageHeader
        title="Statements"
        subtitle="Imported bank/card statements with auto-matched expense entries."
        action={
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-statement-btn"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Upload className="mr-1 h-4 w-4" /> Upload statement
          </Button>
        }
      />
      {triageBanner}
      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="batches-table">
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Card</TableHead>
              <TableHead>Uploaded by</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Totals (M / A / U)</TableHead>
              {isAdmin && <TableHead className="text-right w-[60px]"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} className="py-10 text-center text-slate-500">
                  <FileSpreadsheet className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                  No statements uploaded yet — drop your first CSV.
                </TableCell>
              </TableRow>
            ) : (
              batches.map((b) => (
                <TableRow key={b.id} data-testid={`batch-row-${b.id}`} className="cursor-pointer hover:bg-slate-50">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {b.source_type === "plaid" ? (
                        <Badge
                          data-testid={`batch-source-plaid-${b.id}`}
                          className="border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-50">
                          <Banknote className="mr-1 h-3 w-3" /> Plaid
                        </Badge>
                      ) : (
                        <Badge
                          data-testid={`batch-source-csv-${b.id}`}
                          variant="secondary"
                          className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                          <FileText className="mr-1 h-3 w-3" /> CSV
                        </Badge>
                      )}
                      <Link to={`/statements/${b.id}`} className="text-[#0F1E3D] hover:underline" data-testid={`batch-link-${b.id}`}>
                        {b.original_filename}
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{b.card_label} · <span className="font-mono">{b.card_last4}</span></TableCell>
                  <TableCell className="text-sm text-slate-600">{b.uploaded_by_name || b.uploaded_by_email || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDateTime(b.uploaded_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {b.period_start ? `${formatDate(b.period_start)} – ${formatDate(b.period_end)}` : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_STYLE[b.status] || "bg-slate-100 text-slate-700"} hover:opacity-90`}>{b.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <span className="text-emerald-700">{b.matched_count}</span>
                    {" / "}
                    <span className="text-amber-700">{b.ambiguous_count}</span>
                    {" / "}
                    <span className="text-rose-700">{b.unmatched_count}</span>
                    <span className="ml-1 text-slate-400">of {b.total_lines}</span>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="icon"
                        className="h-8 w-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(b); }}
                        data-testid={`batch-delete-btn-${b.id}`}
                        aria-label="Delete batch">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <UploadWizard open={uploadOpen} onOpenChange={setUploadOpen} onCreated={() => load()} />

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && !deleting && setDeleteTarget(null)}>
        <DialogContent data-testid="batch-delete-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete batch?</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{deleteTarget?.original_filename}</span>
              {" "}— this will delete <strong>{deleteTarget?.total_lines || 0}</strong> line(s),
              revert <strong>{deleteTarget?.matched_count || 0}</strong> matched expense(s) to pending,
              and cancel any open chases tied to this batch. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="batch-delete-confirm-btn"
              className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? "Deleting…" : "Delete batch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
